import { cellAtWorld } from '../grid.ts';
import { idiv, imax, mulBp } from '../math/fixed.ts';
import type { BattleState, Hero } from '../types.ts';
import type { Pt } from '../caza-types.ts';
import type { LeapsRules, LeapsState } from '../vertebras-types.ts';
import { damageHero, ev } from './combat.ts';
import { leapsAdj, leapsSolve, type LeapsPos } from './vertebras-layout.ts';

/**
 * The leaps (docs/sendas/barbaro.md, chapter 2, act 2; §4, «Los arcos»; the ground in battle/vertebras-layout.ts). Ten
 * arches over the sea and the last vertebra, joined by gaps; across each hangs Sigrun's old rope: a tap on the arch
 * across and the hero swings over (Salto or Carga over the gap count as well: wherever the hero lands is an arrival).
 * Each arch bears two arrivals and falls at the third, the hero with it (hurt, back on the arch it left); the first
 * arch counts one already. Three arches carry Sigrun's mark (five strokes and a knot): the vertebra reached with the
 * three touched wins it. A mark whose arch falls before it was touched goes to another, far off (from the seed). If no
 * way through is left, the act is lost a moment later («Los arcos se caen solos»).
 *
 * Cost: an arrival is looked at once; the stuck check walks the puzzle's positions (a few thousand at most) only then.
 */

const fresh = (r: LeapsRules): LeapsState => {
  const n = r.arches.length;
  const cracks = Array.from({ length: n }, () => 0);
  cracks[r.start] = 1;
  return { on: r.start, cracks, fallen: cracks.map(() => false), marks: r.marks.slice(), touched: r.marks.map(() => false), to: -1, from: { x: 0, y: 0 }, t0: 0, falls: 0, moved: 0, done: -1, stuck: -1 };
};

/** The vertebra's index (the last). */
export const leapsVert = (r: LeapsRules): number => r.arches.length - 1;

/** The arch a cell is of (−1: the sea). */
export const leapsArchOf = (r: LeapsRules, cell: number): number => {
  let lo = 0;
  let hi = r.cells.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const c = r.cells[mid] as number;
    if (c === cell) return r.cellArch[mid] as number;
    if (c < cell) lo = mid + 1;
    else hi = mid - 1;
  }
  return -1;
};

/** Whether a gap joins two arches. */
export const leapsJoined = (r: LeapsRules, a: number, b: number): boolean => {
  const lo = a < b ? a : b;
  const hi = a < b ? b : a;
  return r.edges.some(([x, y]) => x === lo && y === hi);
};

/** The puzzle's position as it stands (for the solver: fallen arches are three arrivals). */
export const leapsPos = (w: LeapsState): LeapsPos => {
  const touched = w.touched.reduce((m, t, i) => (t ? m | (1 << i) : m), 0);
  // Touched marks need nothing more: off the board for the solver (an arch that isn't one).
  const marks = w.marks.map((m, i) => (w.touched[i] ? -1 : m));
  return { on: w.on, cracks: w.cracks.map((c, i) => (w.fallen[i] ? 3 : c)), touched, marks };
};

/** The shortest way through from here (null: none left). */
export const leapsWay = (r: LeapsRules, w: LeapsState): number[] | null => leapsSolve(leapsAdj(r.arches.length, r.edges), leapsPos(w));

/** A tap on an arch: across the gap by Sigrun's rope, if one joins it to the hero's. */
export const leapsOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.vert?.leaps;
  const w = s.senda?.vert?.leaps;
  if (!r || !w || w.done >= 0 || w.stuck >= 0 || w.to >= 0 || h.downTicks > 0) return;
  if (at === w.on || at < 0 || at >= r.arches.length || w.fallen[at] || !leapsJoined(r, w.on, at)) {
    ev(s, { t: 'senda', kind: 'vRope', npc: '', x: h.x, y: h.y, n: -1 });
    return;
  }
  w.to = at;
  w.from = { x: h.x, y: h.y };
  w.t0 = s.tick;
  h.path = [];
  h.moving = false;
  h.target = 0;
  h.forced = false;
  const p = r.arches[at] as Pt;
  ev(s, { t: 'senda', kind: 'vRope', npc: '', x: p.x, y: p.y, n: at });
};

/** A mark's arch fell before it was touched: the mark goes to another, as far off as can be that still leaves a way. */
const moveMark = (s: BattleState, r: LeapsRules, w: LeapsState, i: number, cue: (c: string) => void): void => {
  const v = leapsVert(r);
  const adj = leapsAdj(r.arches.length, r.edges);
  // Gap-distance from the hero over the arches still standing.
  const dist = r.arches.map(() => -1);
  dist[w.on] = 0;
  const q = [w.on];
  for (let k = 0; k < q.length; k++) {
    const a = q[k] as number;
    for (const b of adj[a] as number[])
      if (dist[b] === -1 && !w.fallen[b]) {
        dist[b] = (dist[a] as number) + 1;
        q.push(b);
      }
  }
  const cands = r.arches
    .map((_, k) => k)
    .filter((k) => k !== v && k !== w.on && !w.fallen[k] && (dist[k] as number) > 0 && !w.marks.some((m, j) => m === k && !w.touched[j]))
    .sort((a, b) => (dist[b] as number) - (dist[a] as number) || (w.cracks[a] as number) - (w.cracks[b] as number) || a - b);
  const from = w.marks[i] as number;
  let to = cands[0] ?? -1;
  for (const k of cands) {
    w.marks[i] = k;
    if (leapsWay(r, w)) {
      to = k;
      break;
    }
  }
  w.marks[i] = to;
  if (to < 0) return;
  w.moved++;
  const p = r.arches[to] as Pt;
  ev(s, { t: 'senda', kind: 'vMarkMoved', npc: '', x: p.x, y: p.y, n: to * 100 + from });
  cue(`moved${w.moved}`);
};

/** The hero comes onto an arch (by the rope or by a leap) from the one it was on. */
const arrive = (s: BattleState, r: LeapsRules, w: LeapsState, h: Hero, k: number, cue: (c: string) => void): void => {
  const v = leapsVert(r);
  const p = r.arches[k] as Pt;
  if (k === v) {
    w.on = k;
    ev(s, { t: 'senda', kind: 'vArrive', npc: '', x: p.x, y: p.y, n: k * 10 });
    if (w.touched.every((t) => t)) {
      w.done = s.tick;
      ev(s, { t: 'senda', kind: 'vLeapsDone', npc: '', x: p.x, y: p.y });
    } else cue('early');
    return;
  }
  const c = (w.cracks[k] as number) + 1;
  w.cracks[k] = c;
  ev(s, { t: 'senda', kind: 'vArrive', npc: '', x: p.x, y: p.y, n: k * 10 + c });
  if (c === 2) cue('crack2');
  if (c >= 3) {
    // Down it goes, and the hero with it: hauled back onto the arch it left, hurt.
    w.fallen[k] = true;
    w.falls++;
    ev(s, { t: 'senda', kind: 'vArchFall', npc: '', x: p.x, y: p.y, n: k });
    damageHero(s, h, mulBp(h.stats.maxHp, r.fallBp), null, 'physical', true);
    const b = r.arches[w.on] as Pt;
    h.x = h.destX = h.ax = b.x;
    h.y = h.destY = h.ay = b.y + 200;
    h.path = [];
    h.moving = false;
    h.stun = imax(h.stun, 12);
    cue(`fall${w.falls}`);
    w.marks.forEach((m, i) => {
      if (m === k && !w.touched[i]) moveMark(s, r, w, i, cue);
    });
  } else {
    w.on = k;
    w.marks.forEach((m, i) => {
      if (m !== k || w.touched[i]) return;
      w.touched[i] = true;
      ev(s, { t: 'senda', kind: 'vTouch', npc: '', x: p.x, y: p.y, n: w.touched.filter((t) => t).length });
      cue(`mark${w.touched.filter((t) => t).length}`);
    });
  }
  if (!leapsWay(r, w)) {
    w.stuck = s.tick;
    ev(s, { t: 'senda', kind: 'vStuck', npc: '', x: h.x, y: h.y });
    cue('stuck');
  }
};

export const leapsTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.vert?.leaps;
  const st = s.senda;
  if (!r || !st) return;
  const vs = (st.vert ??= {});
  const w = (vs.leaps ??= fresh(r));
  if (!s.started || w.done >= 0 || w.stuck >= 0) return;
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h) return;
  if (w.to >= 0) {
    // Over the gap on the rope: from where it stood to the arch's middle, a little sag in the middle (the drawing's).
    const p = r.arches[w.to] as Pt;
    const k = imax(0, s.tick - w.t0);
    const x = w.from.x + idiv((p.x - w.from.x) * k, r.rope);
    const y = w.from.y + idiv((p.y - w.from.y) * k, r.rope);
    h.x = h.destX = h.ax = x;
    h.y = h.destY = h.ay = y;
    h.path = [];
    h.moving = false;
    if (k >= r.rope) {
      const to = w.to;
      w.to = -1;
      arrive(s, r, w, h, to, cue);
    }
    return;
  }
  // A leap or a charge over a gap: wherever it lands is an arrival.
  const a = leapsArchOf(r, cellAtWorld(s, h.x, h.y));
  if (a >= 0 && a !== w.on && !w.fallen[a]) arrive(s, r, w, h, a, cue);
};

/** The marks touched and the vertebra reached. */
export const leapsGoal = (s: BattleState): { done: number; need: number } => {
  const w = s.senda?.vert?.leaps;
  if (!w) return { done: 0, need: 4 };
  return { done: w.touched.filter((t) => t).length + (w.done >= 0 ? 1 : 0), need: w.touched.length + 1 };
};

/** Won at the vertebra (the arches go into the sea behind, a moment); lost with no way through left. */
export const leapsResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = s.senda?.vert?.leaps;
  if (!w) return null;
  if (w.done >= 0 && s.tick >= w.done + 48) return 'won';
  if (w.stuck >= 0 && s.tick >= w.stuck + 60) return 'lost';
  return null;
};
